import { IdesMicroWorkerMessages } from './enums/messages';
import { IImageResult } from './interfaces/image';
import { IWorkerMessageEvent } from './interfaces/message';
import EventService from '../helpers/classes/EventService';
import { JourneyEvent } from '../idscan-jcs';

export class IdesMicroHost {

	private idesMicroWorker?: Worker;

	public isWorkerSupported = () => typeof (Worker) !== 'undefined';

	public createWorker = (workerScriptUrl: string, asmScriptUrl: string, timeout: number, timeLogsEnabled: boolean): Promise<void> => new Promise((resolve, reject) => {

		if (this.idesMicroWorker) {
			return this.makeRequest(IdesMicroWorkerMessages.Initialise, { asmScriptUrl, timeout, timeLogsEnabled})
				.then(resolve)
				.catch(reject);
		} else {
			EventService.publish(JourneyEvent.TRANSFER_STARTED, {
				name: 'ides',
				status: 'started',
				percentage: 0,
			})
				.then(() => this.idesMicroWorker =  this.createCrossDomainWorker(workerScriptUrl))
				.then(() => this.makeRequest(IdesMicroWorkerMessages.Initialise, { asmScriptUrl, timeout, timeLogsEnabled }))
				.then(() => EventService.publish(JourneyEvent.TRANSFER_COMPLETE, {
					name: 'ides',
					status: 'complete',
					percentage: 100,
				}))
				.then(resolve)
				.catch(reject);
		}
	})

	private createCrossDomainWorker(workerUrl: string) : Worker | undefined {
		var worker;
		try {
			if (this.testSameOrigin(workerUrl)) {
				worker = new Worker(workerUrl);
				worker.onerror =  (event) => {
					event.preventDefault();
					worker = this.createWorkerFallback(workerUrl);
				};
			} else {
				worker = this.createWorkerFallback(workerUrl);
			}
		} catch (e) {
			worker = this.createWorkerFallback(workerUrl);
		}
		return worker;
	}

	private createWorkerFallback (workerUrl: string) : Worker | undefined {
		var worker;
		try
		{
			var blob;
			try {
				blob = new Blob(["importScripts('" + workerUrl + "');"], { "type": 'text/javascript' });
			}
			catch (blobEx) {
				var blobBuilder = new (window && (window as any).BlobBuilder || window && (window as any).WebKitBlobBuilder || window && (window as any).MozBlobBuilder)();
				blobBuilder.append("importScripts('" + workerUrl + "');");
				blob = blobBuilder.getBlob('text/javascript');
			}

			var url = window && (window as any).URL || window && (window as any).webkitURL;
			var blobUrl = url.createObjectURL(blob);
			return new Worker(blobUrl);
		} catch (e1) {
			console.log('WorkerFallback', e1);
		}
		return worker;
	}

	public createNewWorker = (workerScriptUrl: string, asmScriptUrl: string, timeout: number, timeLogsEnabled: boolean) => this.destroyWorker()
		.then(() => this.createWorker(workerScriptUrl, asmScriptUrl, timeout, timeLogsEnabled))

	public destroyWorker = () => new Promise<void>((resolve) => {
		if (this.idesMicroWorker) {
			return this.makeRequest(IdesMicroWorkerMessages.Terminate, {})
				.then(() => this.idesMicroWorker!.terminate())
				.then(() => this.idesMicroWorker = undefined)
				.then(resolve);
		} else {
			resolve();
		}
	})

	public checkImage = (imageBlob: Blob): Promise<IImageResult> => this.generateFile(imageBlob)
		.then((image) => this.makeRequest(IdesMicroWorkerMessages.PerformQualityCheck, { image }))

	private makeRequest = <I = any, O = any>(
		requestMessage: IdesMicroWorkerMessages,
		requestData: I,
		requestId: string = new Date().getTime().toString()
	): Promise<O> => new Promise((resolve, reject) => {
		if (this.idesMicroWorker) {
			const responseHandler = ({ data }: IWorkerMessageEvent<O>) => {
				if (data.id === requestId) {
					this.idesMicroWorker!.removeEventListener('message', responseHandler);
					this.idesMicroWorker!.removeEventListener('error', errorHandler);

					switch (data.message) {
						case IdesMicroWorkerMessages.Error:
							return reject();
						default:
							return resolve(data.data);
					}
				}
			};

			const errorHandler = (e: any) => {
				this.idesMicroWorker!.removeEventListener('message', responseHandler);
				this.idesMicroWorker!.removeEventListener('error', errorHandler);

				return reject(e);
			};

			this.idesMicroWorker.addEventListener('message', responseHandler);
			this.idesMicroWorker.addEventListener('error', errorHandler);

			this.idesMicroWorker!.postMessage({
				id: requestId,
				message: requestMessage,
				data: requestData,

			});
		} else {
			reject();
		}
	})

	private generateFile = (blob: Blob): Promise<Uint8Array> => new Promise((resolve, reject) => {
		const fileReader = new FileReader();

		fileReader.addEventListener('load', (e) => {
			resolve(new Uint8Array((e.target as FileReader).result as ArrayBuffer));
		});

		fileReader.addEventListener('error', reject);

		fileReader.readAsArrayBuffer(blob);
	})


	private testSameOrigin (url: string) {
		var loc = window.location;
		var a = document.createElement('a');
		a.href = url;
		return a.hostname === loc.hostname && a.port === loc.port && a.protocol === loc.protocol;
	}

}

// TODO: Ideally this string wants to come from webpack...
export default new IdesMicroHost();
